import { get, post, put, del } from 'aws-amplify/api';
import { getUserToken } from './util.service';
import { Quote } from '../models/quote.model';
import { CreateQuote } from '../models/createQuote.model';
import type { QuoteAuditLogResponse } from '../models/quoteAudit.model';

export interface GetQuotesResponse {
  items: Quote[];
  lastKey?: string;
}

export const createQuote = async (createQuote: CreateQuote): Promise<Quote> => {
  const userToken = await getUserToken();
  const response = await post({
    apiName: 'quotes',
    path: '/quotes',
    options: {
      body: createQuote as any,
      headers: { 'x-auth-token': `Bearer ${userToken}` },
    },
  }).response;
  const json = (await response.body.json()) as any;
  return json;
};

export const getQuotes = async (filters?: {
  lastName?: string;
  status?: string;
  limit?: number;
  lastKey?: string;
}): Promise<GetQuotesResponse> => {
  const params = new URLSearchParams();
  if (filters?.lastName) params.append('lastName', filters.lastName);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.lastKey) params.append('lastKey', filters.lastKey);

  const queryString = params.toString();
  const path = queryString ? `/quotes?${queryString}` : '/quotes';

  const response = await get({
    apiName: 'quotes',
    path,
  }).response;
  const json = (await response.body.json()) as any;
  return json;
};

export const getQuote = async (quoteId: string): Promise<Quote> => {
  const userToken = await getUserToken();
  const response = await get({
    apiName: 'quotes',
    path: `/quotes/${quoteId}`,
    options: { headers: { 'x-auth-token': `Bearer ${userToken}` } },
  }).response;
  const json = (await response.body.json()) as any;
  return json;
};

export const updateQuote = async (quoteId: string, updateQuote: Partial<CreateQuote>): Promise<void> => {
  const userToken = await getUserToken();
  await put({
    apiName: 'quotes',
    path: `/quotes/${quoteId}`,
    options: {
      body: updateQuote as any,
      headers: { 'x-auth-token': `Bearer ${userToken}` },
    },
  }).response;
};

export const deleteQuote = async (quoteId: string): Promise<void> => {
  const userToken = await getUserToken();
  await del({
    apiName: 'quotes',
    path: `/quotes/${quoteId}`,
    options: { headers: { 'x-auth-token': `Bearer ${userToken}` } },
  }).response;
};

export const getQuoteAuditLog = async (
  quoteId: string,
  options?: { limit?: number; lastKey?: Record<string, unknown> },
): Promise<QuoteAuditLogResponse> => {
  const params = new URLSearchParams();
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.lastKey !== undefined) params.set('lastKey', encodeURIComponent(JSON.stringify(options.lastKey)));
  const query = params.toString();
  const path = query ? `/quotes/${quoteId}/audit-log?${query}` : `/quotes/${quoteId}/audit-log`;
  const userToken = await getUserToken();
  const response = await get({
    apiName: 'quotes',
    path,
    options: { headers: { 'x-auth-token': `Bearer ${userToken}` } },
  }).response;
  const json = (await response.body.json()) as unknown as QuoteAuditLogResponse;
  return json;
};
